import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as xlsx from 'xlsx';

interface UseRestaurantFileUploadOptions {
  onRestaurantsExtracted: (restaurants: string[]) => void;
  onError: (message: string) => void;
}

interface UseRestaurantFileUploadReturn {
  uploadedFile: File | null;
  restaurantCount: number;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ALLOWED_EXTENSIONS = ['csv', 'xlsx', 'xls'] as const;

export const useRestaurantFileUpload = ({
  onRestaurantsExtracted,
  onError,
}: UseRestaurantFileUploadOptions): UseRestaurantFileUploadReturn => {
  const { t } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [restaurantCount, setRestaurantCount] = useState<number>(0);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (
        !ALLOWED_EXTENSIONS.includes(
          fileExtension as (typeof ALLOWED_EXTENSIONS)[number],
        )
      ) {
        onError('Please upload a valid CSV or Excel file');
        return;
      }

      setUploadedFile(file);

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = e.target?.result;
          let restaurantNums: string[] = [];

          if (fileExtension === 'csv') {
            restaurantNums = parseCSVFile(data as string);
          } else {
            restaurantNums = parseExcelFile(data as ArrayBuffer);
          }

          setRestaurantCount(restaurantNums.length);
          onRestaurantsExtracted(restaurantNums);
        } catch (error) {
          onError(t('attributeMassUpdate.fileParsingError'));
        }
      };

      if (fileExtension === 'csv') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }

      // Reset the input value to allow uploading the same file again
      event.target.value = '';
    },
    [onRestaurantsExtracted, onError, t],
  );

  return {
    uploadedFile,
    restaurantCount,
    handleFileUpload,
  };
};

function parseCSVFile(text: string): string[] {
  const lines = text.split('\n').filter(line => line.trim());

  // Check if first row is header by checking if it's numeric
  const firstValue = lines[0].split(',')[0].trim();
  const hasHeader = isNaN(Number(firstValue));

  // Extract restaurant numbers from first column
  const startIndex = hasHeader ? 1 : 0;
  return lines
    .slice(startIndex)
    .map(line => line.split(',')[0].trim())
    .filter(val => !isNaN(Number(val)) && val !== '');
}

function parseExcelFile(data: ArrayBuffer): string[] {
  const workbook = xlsx.read(data, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData: string[][] = xlsx.utils.sheet_to_json(firstSheet, {
    header: 1,
    raw: false,
  });

  // Check if first row is header by checking if it's numeric
  const firstValue = jsonData[0]?.[0] || '';
  const hasHeader = isNaN(Number(firstValue));

  // Extract restaurant numbers from first column
  const startIndex = hasHeader ? 1 : 0;
  return jsonData
    .slice(startIndex)
    .map(row => String(row[0] || '').trim())
    .filter(val => !isNaN(Number(val)) && val !== '');
}
